/**
 * Pure, side-effect-free two-phase map-click resolution used by `useMapClickHandler`.
 * Decides what a click resolves to; the `handler` executes the result.
 *
 * Phase 1 - ordering: candidates are sorted once by MAP_CLICK_PRIORITY_ORDER.
 *
 * Phase 2 - candidacy: a straight-line pipeline of pure, order-preserving stages:
 *   excludeMutual -> resolveTerminal -> deferMixedNonPointTargets -> classifyMode
 * Stages only filter, tag, or classify - none reorder.
 *
 * `assertOrderedByPolicy` enforces correctness: any multi-target result stays in policy
 *  order. A terminal short-circuit only inspects the head (see `resolveTerminal`).
 */
import type { FeatureLayerGeomType } from 'features/mapping/mapping.types';
import {
  DRAWER_ENTITY_POI_TYPES,
  getPolicyIndex,
  MapClickTargetType,
  MUTUALLY_EXCLUSIVE_POI_TYPES,
  NAVIGATIONAL_TYPES,
  ROUTER_ENTITY_TYPES,
} from './priority';

export type MapClickCategory =
  | 'navigational'
  | 'router'
  | 'poi'
  | 'drawerEntity';

export type ClickGeometry = FeatureLayerGeomType;

export type ClickCandidate = {
  type: MapClickTargetType;
  geometry: ClickGeometry;
};

export type GeometryFlags = {
  nonPointCount: number;
  hasNonPointCandidate: boolean;
  hasPoint: boolean;
  /** 2+ non-point candidates and no point hit -> the stacked footer path. */
  stacked: boolean;
  /** At least one non-point and one point -> point wins; non-point routers/drawers defer. */
  mixed: boolean;
};

export type ResolveContext = {
  /** A point router entity (open incident/camera/etc. drawer) is currently selected. */
  pointRouterEntityOpen?: boolean;
};

export type ClickResolution =
  | { mode: 'empty' }
  | { mode: 'navigate'; target: ClickCandidate }
  | { mode: 'overlap'; ordered: ClickCandidate[] }
  | {
      mode: 'select';
      dialog: ClickCandidate[];
      drawer: ClickCandidate[];
      /**
       * True when the click was mixed (area + point) and all drawer entities were deferred.
       * The executor clears any stale drawer entity selection in that case.
       */
      clearDeferredDrawer: boolean;
    };

const isOneOf = (
  types: readonly MapClickTargetType[],
  type: MapClickTargetType,
): boolean => types.includes(type);

const categoryOf = (type: MapClickTargetType): MapClickCategory => {
  if (isOneOf(NAVIGATIONAL_TYPES, type)) return 'navigational';
  if (isOneOf(ROUTER_ENTITY_TYPES, type)) return 'router';
  if (isOneOf(DRAWER_ENTITY_POI_TYPES, type)) return 'drawerEntity';
  return 'poi';
};

const isTerminal = (candidate: ClickCandidate): boolean => {
  const category = categoryOf(candidate.type);
  if (category === 'navigational') return true;
  if (category === 'router') return candidate.geometry === 'point';
  return false;
};

const byPolicy = (a: ClickCandidate, b: ClickCandidate): number =>
  getPolicyIndex(a.type) - getPolicyIndex(b.type);

/**
 * Invariant guard: a resolved multi-target list must never regress in policy index.
 */
export const assertOrderedByPolicy = (candidates: ClickCandidate[]): void => {
  for (let i = 1; i < candidates.length; i += 1) {
    if (
      getPolicyIndex(candidates[i - 1].type) >
      getPolicyIndex(candidates[i].type)
    ) {
      throw new Error(
        `Map-click resolution violated policy order: ${candidates
          .map((c) => c.type)
          .join(' -> ')}`,
      );
    }
  }
};

// Candidacy pipeline stages (pure, order-preserving)

/** Derive the set-level geometry facts. Polygons and lines both count as areas. */
export const classifyGeometry = (
  candidates: ClickCandidate[],
): GeometryFlags => {
  const nonPointCount = candidates.filter((c) => c.geometry !== 'point').length;
  const hasPoint = candidates.some((c) => c.geometry === 'point');
  return {
    nonPointCount,
    hasNonPointCandidate: nonPointCount > 0,
    hasPoint,
    stacked: nonPointCount > 1 && !hasPoint,
    mixed: nonPointCount > 0 && hasPoint,
  };
};

/** Drop the lower-priority member of any mutually exclusive pair (keeps the earlier). */
export const excludeMutual = (
  candidates: ClickCandidate[],
): ClickCandidate[] => {
  const kept: ClickCandidate[] = [];
  for (const candidate of candidates) {
    const excluded = kept.some(
      (k) =>
        (MUTUALLY_EXCLUSIVE_POI_TYPES[k.type] ?? []).includes(candidate.type) ||
        (MUTUALLY_EXCLUSIVE_POI_TYPES[candidate.type] ?? []).includes(k.type),
    );
    if (!excluded) kept.push(candidate);
  }
  return kept;
};

/**
 * Navigate if the top candidate is terminal. Head-only: terminal types always
 * outrank non-terminal, so only `candidates[0]` matters.
 */
export const resolveTerminal = (
  candidates: ClickCandidate[],
): ClickResolution | null => {
  const target = candidates[0];
  if (!target || !isTerminal(target)) return null;
  return { mode: 'navigate', target };
};

/**
 * Point-beats-area. In a mixed click, non-point routers and non-point drawer entities are not
 * eligible, so point targets win. POIs are always kept.
 */
export const deferMixedNonPointTargets = (
  candidates: ClickCandidate[],
  geometry: GeometryFlags,
): ClickCandidate[] => {
  if (!geometry.mixed) return candidates;
  return candidates.filter((candidate) => {
    const category = categoryOf(candidate.type);
    if (category === 'router') return false;
    if (category === 'drawerEntity') return candidate.geometry === 'point';
    return true;
  });
};

/** Choose the interaction mode for the surviving eligible candidate set. */
export const classifyMode = (
  eligible: ClickCandidate[],
  geometry: GeometryFlags,
  context: ResolveContext = {},
  clearDeferredDrawer = false,
): ClickResolution => {
  const routers = eligible.filter((c) => categoryOf(c.type) === 'router');
  const pois = eligible.filter((c) => categoryOf(c.type) === 'poi');
  const drawers = eligible.filter((c) => categoryOf(c.type) === 'drawerEntity');

  // Suppression: a point router drawer is open and the click is only POIs -> don't take
  // over with a stacked footer; show them as a dialog alongside the open drawer instead.
  const isOnlyPois =
    pois.length > 0 && routers.length === 0 && drawers.length === 0;
  const suppressStacked = Boolean(context.pointRouterEntityOpen) && isOnlyPois;

  if (geometry.stacked && !suppressStacked) {
    return { mode: 'overlap', ordered: eligible };
  }

  // A non-point router (alert - polygon) that reached here is neither mixed nor stacked -> it wins.
  if (routers.length) return { mode: 'navigate', target: routers[0] };

  // Area + POI + drawer mix -> Next/Prev footer.
  if (geometry.hasNonPointCandidate && pois.length > 0 && drawers.length > 0) {
    return { mode: 'overlap', ordered: eligible };
  }

  // externalGeoEvent: alone it activates its card directly (no dialog); alongside
  // anything else it joins the Next/Prev footer.
  const externalGeoEvent = pois.find((p) => p.type === 'externalGeoEvent');
  if (externalGeoEvent) {
    return eligible.length === 1
      ? { mode: 'navigate', target: externalGeoEvent }
      : { mode: 'overlap', ordered: eligible };
  }

  if (!pois.length && !drawers.length) return { mode: 'empty' };

  // POI dialog and drawer entity can coexist.
  return { mode: 'select', dialog: pois, drawer: drawers, clearDeferredDrawer };
};

export const resolveClick = (
  candidates: ClickCandidate[],
  context: ResolveContext = {},
): ClickResolution => {
  if (!candidates.length) return { mode: 'empty' };

  // Phase 1: prioritize once. Everything below only filters/classifies this order.
  const prioritized = [...candidates].sort(byPolicy);

  // Phase 2: candidacy pipeline.
  const inPlay = excludeMutual(prioritized);
  const geometry = classifyGeometry(inPlay);

  const terminal = resolveTerminal(inPlay);
  if (terminal) return terminal;

  const eligible = deferMixedNonPointTargets(inPlay, geometry);
  if (!eligible.length) return { mode: 'empty' };

  assertOrderedByPolicy(eligible);

  const isDrawer = (candidate: ClickCandidate): boolean =>
    categoryOf(candidate.type) === 'drawerEntity';
  const clearDeferredDrawer =
    geometry.mixed && inPlay.some(isDrawer) && !eligible.some(isDrawer);

  return classifyMode(eligible, geometry, context, clearDeferredDrawer);
};
